import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../components/cart/cart-context';
import Price from '../components/price';
import Image from '../components/Image';
import LoadingDots from '../components/loading-dots';
import { DEFAULT_OPTION } from 'lib/constants';
import LogoSquare from '../components/logo-square';

export default function CheckoutPage() {
  const { cart } = useCart();
  const [loading, setLoading] = useState(false);
  
  // Liste des pays avec codes et codes ISO
  const countries = [
    { code: '+221', iso: 'SN', name: 'Sénégal' },
    { code: '+223', iso: 'ML', name: 'Mali' },
    { code: '+225', iso: 'CI', name: 'Côte d\'Ivoire' },
    { code: '+226', iso: 'BF', name: 'Burkina Faso' },
    { code: '+227', iso: 'NE', name: 'Niger' },
    { code: '+224', iso: 'GN', name: 'Guinée' },
    { code: '+33', iso: 'FR', name: 'France' },
    { code: '+1', iso: 'US', name: 'États-Unis' },
    { code: '+44', iso: 'GB', name: 'Royaume-Uni' },
    { code: '+49', iso: 'DE', name: 'Allemagne' },
    { code: '+39', iso: 'IT', name: 'Italie' },
    { code: '+34', iso: 'ES', name: 'Espagne' },
    { code: '+32', iso: 'BE', name: 'Belgique' },
    { code: '+41', iso: 'CH', name: 'Suisse' },
    { code: '+31', iso: 'NL', name: 'Pays-Bas' },
    { code: '+351', iso: 'PT', name: 'Portugal' },
    { code: '+212', iso: 'MA', name: 'Maroc' },
    { code: '+213', iso: 'DZ', name: 'Algérie' },
    { code: '+216', iso: 'TN', name: 'Tunisie' },
    { code: '+20', iso: 'EG', name: 'Égypte' },
    { code: '+234', iso: 'NG', name: 'Nigeria' },
    { code: '+254', iso: 'KE', name: 'Kenya' },
    { code: '+27', iso: 'ZA', name: 'Afrique du Sud' },
    { code: '+86', iso: 'CN', name: 'Chine' },
    { code: '+81', iso: 'JP', name: 'Japon' },
    { code: '+82', iso: 'KR', name: 'Corée du Sud' },
    { code: '+91', iso: 'IN', name: 'Inde' },
    { code: '+971', iso: 'AE', name: 'Émirats arabes unis' },
    { code: '+966', iso: 'SA', name: 'Arabie saoudite' },
    { code: '+7', iso: 'RU', name: 'Russie' },
    { code: '+55', iso: 'BR', name: 'Brésil' },
    { code: '+52', iso: 'MX', name: 'Mexique' },
    { code: '+54', iso: 'AR', name: 'Argentine' },
    { code: '+61', iso: 'AU', name: 'Australie' },
    { code: '+64', iso: 'NZ', name: 'Nouvelle-Zélande' },
  ];

  // Form state
  const [email, setEmail] = useState('');
  const [phoneCode, setPhoneCode] = useState('+221'); // Code par défaut pour Sénégal
  const [phoneNumber, setPhoneNumber] = useState('');
  const [newsletter, setNewsletter] = useState(false);
  const [country, setCountry] = useState('Sénégal');
  
  const selectedCountry = countries.find(c => c.code === phoneCode) || countries[0];
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [address, setAddress] = useState('');
  const [apartment, setApartment] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [saveInfo, setSaveInfo] = useState(false);

  if (!cart || cart.lines.length === 0) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-neutral-50 dark:bg-neutral-900">
        <div className="text-center">
          <h1 className="mb-4 text-2xl font-bold text-neutral-900 dark:text-white">
            Votre panier est vide
          </h1>
          <Link
            to="/"
            className="inline-block rounded-md bg-blue-600 px-6 py-3 text-white hover:bg-blue-700"
          >
            Continuer les achats
          </Link>
        </div>
      </div>
    );
  }

  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation : soit email soit téléphone doit être rempli
    if (!email && !phoneNumber) {
      alert('Veuillez saisir un email ou un numéro de téléphone');
      return;
    }
    
    // Validation des champs requis
    if (!lastName || !address || !city) {
      alert('Veuillez remplir tous les champs requis');
      return;
    }
    
    setLoading(true);
    
    // Sauvegarder les données du formulaire
    const formData = {
      email,
      phoneCode,
      phoneNumber,
      country,
      firstName,
      lastName,
      address,
      apartment,
      city,
      state,
      postalCode,
      saveInfo
    };
    
    // Sauvegarder dans localStorage comme backup
    localStorage.setItem('checkoutFormData', JSON.stringify(formData));
    
    // Rediriger vers la page shipping
    navigate('/shipping', { state: formData });
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900">
      <div className="mx-auto flex max-w-7xl">
        {/* Formulaire de checkout - Colonne gauche */}
        <div className="flex-1 border-r border-neutral-200 dark:border-neutral-800">
          <div className="mx-auto max-w-2xl px-6 py-12">
            {/* Logo ShopLink */}
            <div className="mb-4 flex justify-start">
              <Link to="/">
                <LogoSquare />
              </Link>
            </div>
            
            {/* Breadcrumb */}
            <div className="mb-8 flex items-center justify-start space-x-2 text-sm text-neutral-500 dark:text-neutral-400">
              <span className="font-medium text-neutral-900 dark:text-white">Informations</span>
              <span>/</span>
              <span>Livraison</span>
              <span>/</span>
              <span>Paiement</span>
            </div>

            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Section Contact */}
              <div>
                <h2 className="mb-4 text-lg font-semibold text-neutral-900 dark:text-white">
                  Contact
                </h2>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="flex-1">
                      <input
                        type="email"
                        id="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="block w-full rounded-md border border-neutral-300 bg-white px-3 py-2.5 text-neutral-900 shadow-sm transition-colors focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-neutral-600 dark:bg-neutral-800 dark:text-white dark:placeholder-neutral-400 sm:text-sm"
                        placeholder="Email"
                      />
                    </div>
                    <span className="text-sm text-neutral-500 dark:text-neutral-400">ou</span>
                    <div className="flex items-center gap-2 flex-1">
                      <div className="relative w-20">
                        <div className="absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none z-10">
                          <img
                            src={`https://flagcdn.com/w20/${selectedCountry.iso.toLowerCase()}.png`}
                            alt={selectedCountry.name}
                            className="w-5 h-4 object-cover rounded-sm"
                            onError={(e) => {
                              // Fallback si l'image ne charge pas
                              (e.target as HTMLImageElement).style.display = 'none';
                            }}
                          />
                        </div>
                        <select
                          id="phoneCode"
                          value={phoneCode}
                          onChange={(e) => setPhoneCode(e.target.value)}
                          className="appearance-none w-full rounded-md border border-neutral-300 bg-white pl-9 pr-6 py-2.5 text-sm text-neutral-900 shadow-sm transition-colors focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-neutral-600 dark:bg-neutral-800 dark:text-white"
                          aria-label="Code pays"
                        >
                          {countries.map((country) => (
                            <option key={country.code} value={country.code}>
                              {country.code}
                            </option>
                          ))}
                        </select>
                        <div className="absolute right-1.5 top-1/2 -translate-y-1/2 pointer-events-none">
                          <svg className="w-3 h-3 text-neutral-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </div>
                      <input
                        type="tel"
                        id="phoneNumber"
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                        className="flex-1 rounded-md border border-neutral-300 bg-white px-3 py-2.5 text-neutral-900 shadow-sm transition-colors focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-neutral-600 dark:bg-neutral-800 dark:text-white dark:placeholder-neutral-400 sm:text-sm"
                        placeholder="Numéro de téléphone"
                      />
                    </div>
                  </div>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="newsletter"
                      checked={newsletter}
                      onChange={(e) => setNewsletter(e.target.checked)}
                      className="h-4 w-4 rounded border-neutral-300 text-blue-600 focus:ring-blue-500 dark:border-neutral-600 dark:bg-neutral-700"
                    />
                    <label
                      htmlFor="newsletter"
                      className="ml-2 block text-sm text-neutral-700 dark:text-neutral-300"
                    >
                      M'envoyer des emails avec des nouvelles et des offres
                    </label>
                  </div>
                </div>
              </div>

              {/* Section Shipping Address */}
              <div>
                <h2 className="mb-4 text-lg font-semibold text-neutral-900 dark:text-white">
                  Adresse de livraison
                </h2>
                <div className="space-y-4">
                  <div>
                    <label htmlFor="country" className="sr-only">
                      Pays/Région
                    </label>
                    <select
                      id="country"
                      required
                      value={country}
                      onChange={(e) => setCountry(e.target.value)}
                      className="block w-full rounded-md border border-neutral-300 bg-white px-3 py-2.5 text-neutral-900 shadow-sm transition-colors focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-neutral-600 dark:bg-neutral-800 dark:text-white sm:text-sm"
                      aria-label="Pays/Région"
                    >
                      <option value="Sénégal">Sénégal</option>
                      <option value="Mali">Mali</option>
                      <option value="Côte d'Ivoire">Côte d'Ivoire</option>
                      <option value="Burkina Faso">Burkina Faso</option>
                      <option value="Niger">Niger</option>
                      <option value="Guinée">Guinée</option>
                    </select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <input
                        type="text"
                        id="firstName"
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                        className="block w-full rounded-md border border-neutral-300 bg-white px-3 py-2.5 text-neutral-900 shadow-sm transition-colors focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-neutral-600 dark:bg-neutral-800 dark:text-white dark:placeholder-neutral-400 sm:text-sm"
                        placeholder="Prénom (optionnel)"
                      />
                    </div>
                    <div>
                      <input
                        type="text"
                        id="lastName"
                        required
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                        className="block w-full rounded-md border border-neutral-300 bg-white px-3 py-2.5 text-neutral-900 shadow-sm transition-colors focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-neutral-600 dark:bg-neutral-800 dark:text-white dark:placeholder-neutral-400 sm:text-sm"
                        placeholder="Nom de famille"
                      />
                    </div>
                  </div>

                  <div>
                    <input
                      type="text"
                      id="address"
                      required
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                      className="block w-full rounded-md border border-neutral-300 bg-white px-3 py-2.5 text-neutral-900 shadow-sm transition-colors focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-neutral-600 dark:bg-neutral-800 dark:text-white dark:placeholder-neutral-400 sm:text-sm"
                      placeholder="Adresse"
                    />
                  </div>

                  <div>
                    <input
                      type="text"
                      id="apartment"
                      value={apartment}
                      onChange={(e) => setApartment(e.target.value)}
                      className="block w-full rounded-md border border-neutral-300 bg-white px-3 py-2.5 text-neutral-900 shadow-sm transition-colors focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-neutral-600 dark:bg-neutral-800 dark:text-white dark:placeholder-neutral-400 sm:text-sm"
                      placeholder="Appartement, suite, etc. (optionnel)"
                    />
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <input
                        type="text"
                        id="city"
                        required
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                        className="block w-full rounded-md border border-neutral-300 bg-white px-3 py-2.5 text-neutral-900 shadow-sm transition-colors focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-neutral-600 dark:bg-neutral-800 dark:text-white dark:placeholder-neutral-400 sm:text-sm"
                        placeholder="Ville"
                      />
                    </div>
                    <div>
                      <label htmlFor="state" className="sr-only">
                        Région
                      </label>
                      <select
                        id="state"
                        value={state}
                        onChange={(e) => setState(e.target.value)}
                        className="block w-full rounded-md border border-neutral-300 bg-white px-3 py-2.5 text-neutral-900 shadow-sm transition-colors focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-neutral-600 dark:bg-neutral-800 dark:text-white sm:text-sm"
                        aria-label="Région"
                      >
                        <option value="">Région</option>
                        <option value="Dakar">Dakar</option>
                        <option value="Thiès">Thiès</option>
                        <option value="Saint-Louis">Saint-Louis</option>
                        <option value="Ziguinchor">Ziguinchor</option>
                        <option value="Kaolack">Kaolack</option>
                      </select>
                    </div>
                    <div>
                      <input
                        type="text"
                        id="postalCode"
                        value={postalCode}
                        onChange={(e) => setPostalCode(e.target.value)}
                        className="block w-full rounded-md border border-neutral-300 bg-white px-3 py-2.5 text-neutral-900 shadow-sm transition-colors focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-neutral-600 dark:bg-neutral-800 dark:text-white dark:placeholder-neutral-400 sm:text-sm"
                        placeholder="Code postal"
                      />
                    </div>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="saveInfo"
                      checked={saveInfo}
                      onChange={(e) => setSaveInfo(e.target.checked)}
                      className="h-4 w-4 rounded border-neutral-300 text-blue-600 focus:ring-blue-500 dark:border-neutral-600 dark:bg-neutral-700"
                    />
                    <label
                      htmlFor="saveInfo"
                      className="ml-2 block text-sm text-neutral-700 dark:text-neutral-300"
                    >
                      Enregistrer ces informations pour la prochaine fois
                    </label>
                  </div>
                </div>
              </div>

              {/* Bouton Continue to shipping */}
              <button
                type="submit"
                disabled={loading}
                className="w-full rounded-md bg-blue-600 px-4 py-3 text-base font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? <LoadingDots className="bg-white" /> : 'Continuer vers la livraison'}
              </button>
            </form>
          </div>
        </div>

        {/* Order Summary - Colonne droite */}
        <div className="w-96 border-l border-neutral-200 dark:border-neutral-800">
          <div className="sticky top-0 h-screen overflow-y-auto px-6 py-12">
            <h2 className="mb-6 text-lg font-semibold text-neutral-900 dark:text-white">
              Résumé de commande
            </h2>

            <div className="space-y-4">
              {cart.lines.map((item, i) => (
                <div key={i} className="flex items-start space-x-3">
                  <div className="relative h-16 w-16 shrink-0">
                    <div className="h-full w-full overflow-hidden rounded-md border-2 border-blue-500 bg-neutral-100 dark:bg-neutral-800">
                      <Image
                        src={item.merchandise.product.featuredImage?.url}
                        alt={item.merchandise.product.title}
                        width={64}
                        height={64}
                        className="h-full w-full object-cover"
                      />
                    </div>
                    {item.quantity > 1 && (
                      <div className="absolute -right-1 -top-1 z-10 flex h-5 w-5 items-center justify-center rounded-full bg-blue-600 text-xs font-medium text-white shadow-sm">
                        {item.quantity}
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-neutral-900 dark:text-white truncate">
                      {item.merchandise.product.title}
                    </p>
                    {item.merchandise.title !== DEFAULT_OPTION && (
                      <p className="text-sm text-neutral-500 dark:text-neutral-400 truncate">
                        {item.merchandise.title}
                      </p>
                    )}
                    <Price
                      amount={item.cost.totalAmount.amount}
                      currencyCode={item.cost.totalAmount.currencyCode}
                      className="mt-1 text-sm"
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 space-y-3 border-t border-neutral-200 pt-6 dark:border-neutral-700">
              <div className="flex justify-between text-sm text-neutral-600 dark:text-neutral-400">
                <span>Sous-total</span>
                <Price
                  amount={cart.cost.subtotalAmount.amount}
                  currencyCode={cart.cost.subtotalAmount.currencyCode}
                />
              </div>
              <div className="flex justify-between text-sm text-neutral-600 dark:text-neutral-400">
                <span>Livraison</span>
                <span className="text-right">Calculé à l'étape suivante</span>
              </div>
              <div className="flex justify-between border-t border-neutral-200 pt-3 text-xl font-semibold text-neutral-900 dark:border-neutral-700 dark:text-white">
                <span>Total</span>
                <Price
                  amount={cart.cost.totalAmount.amount}
                  currencyCode={cart.cost.totalAmount.currencyCode}
                  className="text-xl"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
